import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import "./app-theme.css";
import { Wordmark } from "./Wordmark";
import { TrialStatusBadge } from "../billing/TrialStatusBadge";
import {
  ChevronDown,
  ChevronRight,
  Moon,
  Plus,
  RefreshCw,
  SignOut,
  Sun,
} from "../../pages/overview/icons";
import { Check, Copy as CopyIcon } from "@phosphor-icons/react";
import { useDarkMode, type UseDarkMode } from "./theme";
import { Avatar } from "../../design-system";
import { AddAgentModal } from "./AddAgentModal";
import { AppsMenu } from "./AppsMenu";
import { useCurrentUser } from "../../identity";
import { workspaceKeyPrefix } from "../../identity/active";
import {
  useOptionalWorkspace,
  useWorkspaceRelativePathname,
  useWsNavigate,
} from "../../workspaces";
import { logout } from "../../auth/logout";
import {
  versionLine,
  versionsVisible,
  webVersion,
} from "../../services/versions";
import { APP_BASENAME, AUTH_ENABLED, LOGIN_PATH } from "../../auth";
import {
  trackButtonPressed,
  trackLogoutPressed,
  trackTimeRangeChanged,
} from "../../analytics/tracking";
import {
  formatBound,
  invalidateQueries,
  useAgentRollups,
  useFilters,
  useIsFetching,
  type RangePreset,
  type TimeRange,
} from "../../analytics";

type Preset = Exclude<RangePreset, "custom">;

const PRESETS: { value: Preset; label: string }[] = [
  { value: "1h", label: "Last hour" },
  { value: "1d", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

function rangeLabel(range: TimeRange): string {
  if (range.preset === "custom") {
    return `${formatBound(range.from)} → ${formatBound(range.to)}`;
  }

  return PRESETS.find((p) => p.value === range.preset)?.label ?? range.preset;
}

/* The app-wide shell — the Claude Design's dark maroon header (wordmark +
 * timeframe + profile + Add Agent) over a light (#F5F4F1, not white) page body.
 * Used as the layout route for every gated page (home, settings, org), so the
 * whole app reads as the product design: no sidebar, dark top bar, light body.
 * Navigation to settings/org lives in the header profile menu. Wires to real
 * app state (filters, current user, logout, onboarding). */
export function AppShell() {
  const [addAgentOpen, setAddAgentOpen] = useState(false);
  const theme = useDarkMode();
  const wsNav = useWsNavigate();

  return (
    <div
      className={`tw-app flex h-screen flex-col overflow-hidden ${theme.shellClass}`}
      style={{ background: "var(--page)", color: "var(--text)" }}
    >
      <Header onAddAgent={() => setAddAgentOpen(true)} theme={theme} />
      {/* Full-bleed content, matching the design (no width cap). The flex
          column pins the footer to the viewport bottom on short pages and
          after the content on long ones. */}
      <main className="relative flex flex-1 flex-col overflow-y-auto px-5 py-4">
        <div className="flex-1">
          <Outlet />
        </div>
        <Footer />
      </main>
      <AddAgentModal
        open={addAgentOpen}
        onClose={() => setAddAgentOpen(false)}
        // Drop the whole workspace cache so the new agent shows everywhere —
        // the overview roster reads the analytics rollups, whose key starts
        // with the filter-query JSON, so an "agents"-prefix invalidation would
        // miss it — then land the user on the new agent's page.
        onAdded={(agentId) => {
          invalidateQueries(workspaceKeyPrefix());
          wsNav(`/agents/${encodeURIComponent(agentId)}`);
        }}
      />
    </div>
  );
}

/* Slim app footer — a quiet hairline row closing every page: the © wordmark
 * and the build versions on the left, external resources on the right.
 * Deliberately minimal (this is a dense dashboard, not a marketing site); it
 * scrolls with the content and sits at the viewport bottom on short pages. */
function Footer() {
  const versionText = versionsVisible()
    ? versionLine({ web: webVersion() })
    : "";

  const links = [
    // { label: "Docs", href: "https://docs.ai21.com" },
    {
      label: "Privacy",
      href: "https://www.ai21.com/terms-policies/privacy-policy/",
    },
    {
      label: "Terms",
      href: "https://www.ai21.com/terms-policies/terms-of-use/",
    },
  ];
  return (
    <footer
      className="-mx-5 -mb-4 mt-10 flex items-center justify-between px-5 py-[11px] text-[11px]"
      style={{
        borderTop: "1px solid var(--border)",
        color: "var(--text-muted)",
      }}
    >
      <span className="tw-mono cursor-default leading-none">
        © {new Date().getFullYear()} AI21 [ Gateway ]
        {versionText ? ` · ${versionText}` : ""}
      </span>
      <nav aria-label="Footer" className="flex items-center gap-5">
        {links.map((l) => (
          <a
            key={l.label}
            href={l.href}
            target="_blank"
            rel="noreferrer"
            className="leading-none transition-colors hover:text-[var(--text)] hover:underline"
            style={{ color: "var(--text-muted)" }}
          >
            {l.label}
          </a>
        ))}
      </nav>
    </footer>
  );
}

/* Which views the header's time controls belong on (OPSS-331). Only the
 * overview and the per-agent drill-down are time-scoped; API keys, billing and
 * org are flat lists, where a "Last 24 hours" chip claimed a filter that does
 * nothing. The selected range lives in the URL + FiltersProvider above this
 * shell, so hiding the control doesn't discard it. */
export function isTimeScopedPath(pathname: string): boolean {
  return pathname === "/" || pathname.startsWith("/agents/");
}

function Header({
  onAddAgent,
  theme,
}: {
  onAddAgent: () => void;
  theme: UseDarkMode;
}) {
  const wsNav = useWsNavigate();
  const timeScoped = isTimeScopedPath(useWorkspaceRelativePathname());

  return (
    <header
      className="tw-topbar flex h-[53px] flex-shrink-0 items-center gap-3 px-6"
      style={{ background: "#33121d" }}
    >
      <div className="flex flex-1 items-center gap-3 text-sm">
        <button
          onClick={() => wsNav("/")}
          aria-label="AI21 Gateway — go to overview"
          className="flex items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          <Wordmark height={16} label="Gateway" color="#fff" bracket="#fff" />
        </button>
        <AgentBreadcrumb />
      </div>

      <TrialStatusBadge />

      {timeScoped && (
        <>
          <TimeframePicker />
          <RefreshButton />
        </>
      )}

      <button
        onClick={() => {
          trackButtonPressed("Add Agent", "agent");
          onAddAgent();
        }}
        className="flex flex-shrink-0 items-center gap-1.5 rounded-[2px] bg-[#771234] px-3 py-1.5 text-[13px] font-medium text-[#fbfbfb] transition-all hover:brightness-110"
      >
        <Plus size={13} /> Connect agent
      </button>

      <AppsMenu />
      <ProfileMenu theme={theme} />
    </header>
  );
}

/* Header breadcrumb — shown only on the per-agent drill-down (matches the Top
 * Bar design's `nav === "configs"` state): `Overview › {agent} agent`, where
 * "Overview" links back. Elsewhere the header is just the wordmark. Route match
 * lives here so the rollups lookup only mounts (and fetches) on an agent page. */
function AgentBreadcrumb() {
  const pathname = useWorkspaceRelativePathname();
  const match = pathname.match(/^\/agents\/([^/]+)/);
  if (!match) return null;

  return <AgentCrumb agentId={decodeURIComponent(match[1])} />;
}

/* The agent route carries only an id, so resolve the display name from the
 * (cached) rollups and fall back to the id for an unregistered/not-yet-loaded
 * agent. Split from AgentBreadcrumb so useAgentRollups runs only on agent
 * pages, not on every page the shell renders. */
function AgentCrumb({ agentId }: { agentId: string }) {
  const wsNav = useWsNavigate();
  const rollups = useAgentRollups();
  const agent = rollups.data?.data.items.find((a) => a.agent_id === agentId);
  const label = agent?.name ?? agentId;

  return (
    <>
      <button
        onClick={() => wsNav("/")}
        className="ml-6 font-medium text-[var(--text-muted)] transition-colors hover:text-[var(--text)]"
      >
        Overview
      </button>
      <ChevronRight size={13} className="text-[var(--text-muted)]" />
      <span className="font-semibold text-[var(--text)]">{label} agent</span>
    </>
  );
}

/* Manual refresh (OPSS-330). Reuses the filters' refresh generation — the same
 * lever DataFreshness pulls — which re-keys every mounted analytics hook and
 * re-resolves relative presets against a fresh clock, so "Last 7 days" slides
 * forward too. The spinner is held for a short grace window as well as while
 * requests are in flight, because the queries only re-key on the commit after
 * the click. */
const REFRESH_SPIN_MS = 600;

function RefreshButton() {
  const { bumpRefresh } = useFilters();
  const fetching = useIsFetching();
  const [pending, setPending] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const spinning = pending || fetching;

  return (
    <button
      onClick={() => {
        trackButtonPressed("Refresh", "dashboard");
        setPending(true);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(
          () => setPending(false),
          REFRESH_SPIN_MS,
        );
        bumpRefresh();
      }}
      aria-label="Refresh data"
      aria-busy={spinning}
      title="Refresh"
      className="flex flex-shrink-0 items-center rounded-[2px] p-1.5 text-[var(--text-muted)] transition-colors hover:text-[var(--text)]"
    >
      <RefreshCw size={14} className={spinning ? "animate-spin" : undefined} />
    </button>
  );
}

function TimeframePicker() {
  const f = useFilters();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2 py-1.5 text-[13px] font-medium text-[var(--text-muted)] transition-colors hover:text-[var(--text)]"
      >
        {rangeLabel(f.range)}
        <ChevronDown size={13} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-1 min-w-[180px] rounded-[5px] bg-[var(--surface)] shadow-lg"
          style={{ border: "1px solid var(--border-2)" }}
        >
          {PRESETS.map((p) => {
            const active = f.range.preset === p.value;
            return (
              <button
                key={p.value}
                onClick={() => {
                  const prev = f.range.preset;
                  if (p.value !== prev) {
                    f.setPreset(p.value);
                    trackTimeRangeChanged(p.value, prev);
                  }

                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-[13px] transition-colors hover:bg-[var(--hover)] ${active ? "font-semibold text-[var(--text)]" : "text-[var(--text)]"}`}
              >
                {p.label}
                {active && (
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--val-crimson)"
                    strokeWidth="2.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* The user menu, per the Figma design (node 284:1158): an always-dark #280611
 * panel hanging from the always-dark bar — independent of the page theme —
 * with 45px rows split by translucent hairlines: name/email, copy-ID rows,
 * API keys, the Dark/Light segmented theme control, and Sign out. */
const MENU_ROW =
  "flex h-[45px] w-full items-center px-[19px] text-left text-[13px] text-white transition-colors hover:bg-[#33121d]";
const MENU_DIVIDER = { borderBottom: "1px solid rgba(255,255,255,0.08)" };

function ProfileMenu({ theme }: { theme: UseDarkMode }) {
  const me = useCurrentUser();
  const workspace = useOptionalWorkspace()?.workspace;
  const wsNav = useWsNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const name = me.name || me.email;

  useEffect(() => {
    if (!open) return;

    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleSignOut = async () => {
    trackLogoutPressed("Sign out");
    try {
      await logout();
    } finally {
      // Full-page navigate to a clean /login (not reload) so the gate re-renders
      // against the cleared session with a fresh query cache. Going straight to
      // /login means the gate has no protected path to capture, so the URL stays
      // bare — no ?redirect=… back to where the user just signed out from.
      window.location.assign(`${APP_BASENAME}${LOGIN_PATH}`);
    }
  };

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Open user menu"
        className="flex items-center rounded-full transition-opacity hover:opacity-90"
      >
        <Avatar
          initials={name.slice(0, 2).toUpperCase()}
          src={me.pictureUrl}
          alt={name}
          size="md"
          gradient={!me.pictureUrl}
        />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-[225px] bg-[#280611] shadow-[0_25px_25px_rgba(0,0,0,0.25)]"
          style={{ border: "1px solid rgba(255,255,255,0.07)" }}
        >
          <div className="px-[19px] pb-4 pt-[15px]" style={MENU_DIVIDER}>
            <p className="truncate text-[13px] font-medium leading-tight text-white">
              {name}
            </p>
            {AUTH_ENABLED && (
              <p
                className="mt-1 truncate text-[11px]"
                style={{ color: "rgba(158,156,149,0.5)" }}
              >
                {me.email}
              </p>
            )}
          </div>
          {AUTH_ENABLED && (
            <>
              <CopyRow label="User ID" value={me.id} />
              {workspace && (
                <CopyRow label="Workspace ID" value={workspace.id} />
              )}
            </>
          )}
          <button
            onClick={() => {
              setOpen(false);
              wsNav("/settings/api-keys");
            }}
            className={MENU_ROW}
            style={MENU_DIVIDER}
          >
            API keys
          </button>
          <button
            onClick={() => {
              setOpen(false);
              trackButtonPressed("Organization");
              wsNav("/org/members");
            }}
            className={MENU_ROW}
            style={MENU_DIVIDER}
          >
            Organization
          </button>
          <ThemeRow theme={theme} />
          {AUTH_ENABLED && (
            <button
              onClick={() => {
                setOpen(false);
                void handleSignOut();
              }}
              className={`${MENU_ROW} gap-2.5`}
            >
              <SignOut size={13} /> Sign out
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* Full-width copy row — the design's "User ID ⧉" line: clicking anywhere on
 * the row copies, with a transient ✓ swap on the trailing icon. */
function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // Clipboard unavailable (permissions/insecure context) — no toast
          // system; the row simply stays uncopied.
        }
      }}
      aria-label={`Copy ${label.toLowerCase()}`}
      className={`${MENU_ROW} justify-between`}
      style={MENU_DIVIDER}
    >
      {label}
      {copied ? (
        <Check size={13} style={{ color: "#8ee3b6" }} />
      ) : (
        <CopyIcon size={13} style={{ color: "rgba(255,255,255,0.75)" }} />
      )}
    </button>
  );
}

/* The design's Dark/Light segmented control: the active mode reads as a
 * raised #33121D chip, the inactive one as muted text. Selecting a mode pins
 * it as an explicit choice (see theme.ts). */
function ThemeRow({ theme: { dark, set } }: { theme: UseDarkMode }) {
  const pick = (next: boolean) => {
    if (next === dark) return;

    // Encode the target mode so PDS can see which direction users go
    // (e.g. whether they seek out light as a default view).
    trackButtonPressed(next ? "Switch to dark mode" : "Switch to light mode");
    set(next);
  };
  const seg = (active: boolean) =>
    `flex h-[26px] items-center gap-1.5 rounded-[5px] px-3 text-[13px] transition-colors ${
      active ? "bg-[#33121d] text-white" : "text-[#614750] hover:text-white"
    }`;

  return (
    <div
      className="flex h-[45px] items-center gap-1 px-[17px]"
      style={MENU_DIVIDER}
    >
      <button onClick={() => pick(true)} className={seg(dark)}>
        <Moon size={13} /> Dark
      </button>
      <button onClick={() => pick(false)} className={seg(!dark)}>
        <Sun size={13} className={dark ? "opacity-50" : undefined} /> Light
      </button>
    </div>
  );
}
